/**
 * Brand lockup — the QR Phone corporate identity: a QR finder-pattern rounded
 * square (the "eye" a scanner locks onto) with its core replaced by the Live
 * Green dot of an active call. No master-brand endorsement, no byline.
 */

import { Box, Typography } from '@mui/material';

type BrandLockupProps = {
  /** 'light' = ink on a light surface (default); 'dark' = white on a dark stage. */
  tone?: 'light' | 'dark';
  /** Symbol height in px (the wordmark scales relative to it). Default 40. */
  size?: number;
  /** Stack the wordmark under the symbol (login) vs. inline (app bar). */
  orientation?: 'horizontal' | 'stacked';
};

/** QR finder-pattern symbol: ink square, paper ring, Live Green dot core. */
export function QrPhoneMark({ size, tone = 'light' }: { size: number; tone?: 'light' | 'dark' }) {
  const squareFill = tone === 'dark' ? '#F6F4ED' : '#16191C';
  const ringStroke = tone === 'dark' ? '#16191C' : '#F6F4ED';

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      focusable="false"
      style={{ flexShrink: 0, display: 'block' }}
    >
      <rect x="0" y="0" width="48" height="48" rx="12" fill={squareFill} />
      <rect x="10" y="10" width="28" height="28" rx="7" fill="none" stroke={ringStroke} strokeWidth="5" />
      <circle cx="24" cy="24" r="6" fill="#16C47F" />
    </svg>
  );
}

/**
 * Rail lockup — the brand name at the top of the docked sidebar. The rail is a
 * solid dark surface, so it reads in white.
 *
 * Text only: the symbol belongs to the full lockup on the sign-in screen, where
 * it has room to be read. Inside the rail it competed with the navigation icons
 * for the same 230px.
 */
export function RailBrand() {
  return (
    <Box component="span" sx={{ fontSize: '1.15rem', fontWeight: 700, letterSpacing: '-0.01em', color: '#FFFFFF' }}>
      QR Phone
    </Box>
  );
}

/** Centered login lockup shown while redirecting to sign-in. */
export default function BrandLockup({ tone = 'light', size = 40, orientation = 'horizontal' }: BrandLockupProps) {
  const isDark = tone === 'dark';
  const nameColor = isDark ? '#FFFFFF' : '#16191C';
  const stacked = orientation === 'stacked';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: 'center',
        gap: stacked ? 1 : 1.25,
        userSelect: 'none',
      }}
    >
      <QrPhoneMark size={size} tone={tone} />
      <Typography
        component="span"
        sx={{
          // Never green — green is reserved for the symbol's core dot / actions.
          color: nameColor,
          fontWeight: 600,
          fontSize: `${Math.round(size * 0.52)}px`,
          letterSpacing: '-0.01em',
          lineHeight: 1.05,
          whiteSpace: 'nowrap',
        }}
      >
        QR Phone
      </Typography>
    </Box>
  );
}
